import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import BaseDialog from '../../../BaseDialog'
import { Grid, Button, Box } from '@mui/material'

import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'
import validators from '../../../../utils/validators'
import AutoCompleteInput from '../../../AutoCompleteInput'

export default function GeneratePVAdmissionDialog({
  open,
  onClose,
  onCreated,
}) {
  const { t } = useTranslation()

  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [nParc, setNParc] = useState('')

  const resetState = () => {
    // Clean form
    setLoading(false)
    setError('')
    setNParc('')
  }

  const onCloseDialog = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose()
    } else {
      resetState()
      onClose()
    }
  }

  const handleExport = async (asDocx) => {
    setError(null)
    if (nParc.length === null) {
      setError('nParc_required')
      return
    }

    if (!validators.isValidNParc(nParc)) {
      setError('nParc_invalid')
      return
    }

    setLoading(true)

    try {
      // Fetch the document using the new method
      const response = await dataProvider.fetchPVAdmissionByParkNumber(
        nParc.title,
        asDocx
      )

      // If successful, create a blob URL and open it in a new tab
      const blob = new Blob([response.data], {
        type: asDocx
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'application/pdf',
      })
      const blobUrl = window.URL.createObjectURL(blob)

      // Open in new tab
      window.open(blobUrl, '_blank')

      // Clean up the blob URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)

      setLoading(false)
      showNotification('success', t('generate-pv-admission-dialog.success'))
      onCreated()
      resetState()
    } catch (err) {
      setLoading(false)

      // If the error response is a blob, convert it to JSON first
      let errorData = err.response?.data

      if (errorData instanceof Blob) {
        try {
          const text = await errorData.text()
          errorData = JSON.parse(text)
        } catch (parseErr) {
          // If parsing fails, fallback to status code handling
          errorData = null
        }
      }

      // Handle validation errors from the backend
      if (errorData?.validationErrors?.length > 0) {
        const validationError = errorData.validationErrors[0]
        showNotification('error', validationError.errorMessage)
        setError(`${validationError.field}_invalid`)
      } else if (errorData?.error === 'CHASSIS_NOT_IN_CONFORMITY') {
        showNotification(
          'error',
          errorData.message || t('errors.chassis-not-in-conformity')
        )
      } else if (
        errorData?.error === 'PV_ADMISSION_EXCLUDED_FOR_VEHICLE_TYPE'
      ) {
        showNotification(
          'error',
          errorData.message || t('errors.vehicle-type-excluded')
        )
      } else if (errorData?.message) {
        showNotification('error', errorData.message)
      } else if (err.response?.status === 404) {
        showNotification('error', t('errors.chassis-not-found'))
      } else if (err.response?.status === 400) {
        showNotification('error', t('errors.invalid-request'))
      } else {
        showNotification('error', t('errors.api-error'))
      }
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={onCloseDialog}
      title={t('generate-pv-admission-dialog.title')}
      loading={loading}
      hideActions={true}
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <AutoCompleteInput
            required
            error={error === 'nParc_required' || error === 'nParc_invalid'}
            helperText={
              error === 'nParc_invalid'
                ? t('errors.not-valid')
                : t('common.required')
            }
            id="nParc"
            label={t('common.nParc')}
            onChange={(val) => {
              setNParc(val)
            }}
            resource="chassis/parc-number-autocomplete"
            value={nParc}
            canDelete
          />
        </Grid>
        <Grid item>
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleExport(false)}
              disabled={loading || !nParc}
            >
              {t('controls-detail.export-pdf')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleExport(true)}
              disabled={loading || !nParc}
            >
              {t('controls-detail.export-docx')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </BaseDialog>
  )
}
